import { MapPin, Navigation } from "lucide-react";
import { motion } from "framer-motion";

const ZONES = [
  { name: "Launaguet", tag: "Base ADS", highlight: true },
  { name: "Aucamville" },
  { name: "Castelginest" },
  { name: "Saint-Alban" },
  { name: "Fenouillet" },
  { name: "Fonbeauzard" },
  { name: "Lespinasse" },
  { name: "Gratentour" },
  { name: "Bruguières" },
  { name: "Pechbonnieu" },
  { name: "Saint-Jory" },
  { name: "L'Union", tag: "Clinique", highlight: true },
  { name: "Castelmaurou" },
  { name: "Montberon" },
  { name: "Balma" },
  { name: "Croix-Daurade" },
  { name: "Borderouge" },
  { name: "Sesquières" },
  { name: "Lalande" },
  { name: "Trois Cocus" },
  { name: "Toulouse Nord", tag: "Secteur", highlight: true },
  { name: "Villeneuve-lès-B." },
  { name: "Fronton" },
  { name: "Grenade" },
];

export default function ZonesSection() {
  return (
    <section id="zones" className="py-20 md:py-28" data-testid="zones-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-14"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-amber-400 font-tagline text-sm tracking-[0.3em] uppercase font-semibold">
            Notre couverture
          </span>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mt-3">
            Zones desservies au Nord de Toulouse
          </h2>
          <p className="text-zinc-400 mt-3 text-base md:text-lg max-w-2xl mx-auto">
            Basés à <strong className="text-amber-400">Launaguet</strong>, nous couvrons l'ensemble des communes du <strong className="text-amber-400">Nord de Toulouse</strong> et de la Haute-Garonne.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {ZONES.map((z, i) => (
            <motion.article
              key={z.name}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border transition-all duration-300 hover:border-amber-500/50 cursor-default ${
                z.highlight
                  ? "border-amber-500/30 bg-amber-500/5"
                  : "border-white/10 bg-card/50 hover:bg-card"
              }`}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: i * 0.03 }}
              whileHover={{ scale: 1.05 }}
              data-testid={`zone-${z.name.toLowerCase().replace(/['\s]/g, "-")}`}
            >
              <MapPin className={`h-5 w-5 ${z.highlight ? "text-amber-400" : "text-zinc-500"}`} />
              <h3 className="text-sm font-semibold text-white text-center">{z.name}</h3>
              {z.tag && (
                <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full">
                  {z.tag}
                </span>
              )}
            </motion.article>
          ))}
        </div>

        {/* SEO content block */}
        <motion.div
          className="mt-14 bg-card/60 border border-white/10 rounded-2xl p-6 md:p-8"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <Navigation className="h-5 w-5 text-amber-400" />
            <h3 className="text-lg font-bold text-white">
              Votre taxi VSL conventionné CPAM au Nord de Toulouse
            </h3>
          </div>
          <div className="text-sm text-zinc-400 leading-relaxed space-y-3">
            <p>
              <strong className="text-zinc-300">RSE TAXI</strong> est le taxi de référence au <strong className="text-zinc-300">Nord de Toulouse</strong>. 
              Basé à <strong className="text-zinc-300">Launaguet (31140)</strong>, nous desservons exclusivement les communes du nord toulousain : 
              <strong className="text-zinc-300">Aucamville, Castelginest, Saint-Alban, 
              Fenouillet, Fonbeauzard, Lespinasse, Gratentour, Bruguières, Pechbonnieu, Saint-Jory, 
              Borderouge, Croix-Daurade, Sesquières, Lalande, Trois Cocus</strong> et toutes les communes environnantes.
            </p>
            <p>
              Spécialiste du <strong className="text-zinc-300">transport médical VSL au Nord de Toulouse</strong>, nous assurons vos trajets vers la 
              <strong className="text-zinc-300">Clinique de l'Union</strong> à L'Union (31240), 
              le <strong className="text-zinc-300">CHU Purpan</strong>, l'<strong className="text-zinc-300">Oncopole</strong>, et tous les centres de soins du secteur nord. 
              Notre agrément <strong className="text-zinc-300">CPAM</strong> vous garantit le <strong className="text-zinc-300">tiers payant</strong> : 
              aucune avance de frais pour les patients pris en charge à 100% (ALD, accident du travail, maternité).
            </p>
            <p>
              Besoin d'un <strong className="text-zinc-300">taxi à Launaguet</strong> ou dans le <strong className="text-zinc-300">Nord de Toulouse</strong> ? 
              Appelez RSE TAXI au <strong className="text-zinc-300">06 52 62 07 47</strong> — disponible <strong className="text-zinc-300">24h/24 et 7j/7</strong>. 
              Que ce soit pour un transport médical VSL, une course professionnelle vers l'aéroport Toulouse-Blagnac, 
              ou un déplacement privé, nous sommes votre chauffeur de confiance au nord de Toulouse.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
